import { Dispatch } from "redux";
import { IListObjectStore } from "../../_common/type";
import { IJobHOCState } from "../../_core/job/types";
import { TMARPOLProcessedTank } from "./@types";

namespace MARPOLModule {
	export interface Store<
		TCurrentOperation,
		TBackendRecord extends { id: string },
		TCustomFilters
	> extends IJobHOCState {
		records: IListObjectStore<TBackendRecord>;
		currentOperation?: TCurrentOperation;
		filters: RecordFilters<TCustomFilters>;
	}

	export type Operation<TMARPOLRecordCode, TMARPOLRecord> = {
		recordCode: TMARPOLRecordCode;
		operationState: OperationState;
		formData: TMARPOLRecord;
		validationAlerts: IListObjectStore<AlertInOperation>;
		processedTanks: IListObjectStore<TMARPOLProcessedTank>;
	};

	export enum OperationState {
		RECORD = "RECORD",
		PREVIEW = "PREVIEW",
	}

	export enum RecordState {
		APPROVED = "APPROVED",
		STRIKED_OFF = "STRIKED-OFF",
		SUBMITTED = "SUBMITTED",
		IDLE = "IDLE",
		EDIT = "EDIT",
		VERIFIED = "VERIFIED",
	}

	export type RecordUser = {
		name: string;
		designation: string;
		staffId: string;
		date: string;
	};

	export type RecordFilters<TExtraFilters> = {
		startDate: string;
		endDate: string;
	} & TExtraFilters;

	export type CommonRecordFields = {
		selectedDate: string;
		remarks: string;
	};

	export type CommonRecordMetadata<Documents> = {
		id: string;
		// It's an ID for latest update for a document with given id.
		_rev: string;
		marpolType: Documents;
		state: RecordState;
		submittedBy?: RecordUser;
		approvedBy?: RecordUser;
		verifiedBy?: RecordUser;
		strikedBy?: RecordUser;
	};

	export type BackendRecord<TMARPOLRecord> = TMARPOLRecord & {
		submittedBy: RecordUser;
		approvedBy?: RecordUser;
		verifiedBy?: RecordUser;
		strikedBy?: RecordUser;
		processedTanks: TMARPOLProcessedTank[];
		nonParticipantTanks: { id: string; expectedQuantity: number }[];
	};

	export type OperationInfo<T> = {
		title: string;
		itemsList: string;
		code: T;
		hidden?: boolean;
	};

	export type OperationsList<T> = {
		[key: string]: {
			groupName: string;
			operations: OperationInfo<T>[];
		};
	};

	export type ObservedQuantity = {
		preOperation: number;
		postOperation: number;
	};

	export enum AlertType {
		WARNING = "WARNING",
		ERROR = "ERROR",
		INFO = "INFO",
		REQUIRED_FIELD = "REQUIRED_FIELD",
		OBSERVED_QUANTITY_ALERT = "OBSERVED_QUANTITY_ALERT",
	}

	export type AlertInOperation = {
		id: string;
		type: AlertType;
		title: string;
		subtitle: string;
		message: string;
		listOfItems?: {
			title: string;
			subtitle?: string;
			message?: string;
			link?: string;
		}[];
		action?: {
			title: string;
			link: string;
		};
	};

	export type OperationLock = {
		isLocked: boolean;
		lockReason: string;
		lockExplanation: string;
		listOfMessages?: {
			header: string;
			messages: { title: string; extraInformation?: string }[];
		};
		listOfSuggestions?: {
			header: string;
			suggestions: { title: string; action(dispatch?: Dispatch): void }[];
		};
	};

	export type OperationLocksList = {
		[key: string]: OperationLock[];
	};

	export type MissedEntryItem<TMissedMARPOLRecord> = {
		dateOfOriginalOperation: string;
		isAnMissedEntryRecord: boolean;
		missedRecord?: TMissedMARPOLRecord;
		receiptAttachment?: string;
	};
}

export default MARPOLModule;
